/**
 * Revenue Analytics Page
 *
 * Enterprise features:
 * - Real-time revenue stats with polling
 * - Infinite scroll transactions list
 * - Revenue by plan breakdown
 * - Revenue trend visualization
 * - CSV export functionality
 */

import { useState, useMemo, useCallback } from 'react';
import StatCard from '../../components/common/StatCard';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import { revenueApi, billingApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useQuery, useInfiniteQuery, useIntersectionObserver } from '../../hooks/useDataFetch';
import './Revenue.css';

// Constants
const STATS_REFRESH_INTERVAL = 60000; // 60 seconds
const TRANSACTIONS_PAGE_SIZE = 20;

const Revenue = () => {
  const { showToast } = useToast();
  const [trendPeriod, setTrendPeriod] = useState('monthly');

  // Fetch revenue dashboard stats with polling
  const {
    data: dashboardData,
    isLoading: loadingDashboard,
    error: dashboardError,
    refetch: refetchDashboard,
  } = useQuery(
    () => revenueApi.dashboard(),
    {
      key: 'revenue:dashboard',
      cacheTTL: 30000,
      refetchInterval: STATS_REFRESH_INTERVAL,
    }
  );

  // Fetch revenue by plan
  const {
    data: planBreakdownData,
    isLoading: loadingPlanBreakdown,
  } = useQuery(
    () => revenueApi.byPlan(),
    {
      key: 'revenue:byPlan',
      cacheTTL: 60000,
    }
  );

  // Fetch revenue trend
  const {
    data: trendData,
    isLoading: loadingTrend,
    refetch: refetchTrend,
  } = useQuery(
    () => revenueApi.trend({ period: trendPeriod }),
    {
      key: `revenue:trend:${trendPeriod}`,
      cacheTTL: 60000,
    }
  );

  // Fetch transactions with cursor pagination
  const {
    data: transactions,
    isLoading: loadingTransactions,
    isFetchingMore,
    hasMore,
    fetchMore,
    refetch: refetchTransactions,
  } = useInfiniteQuery(
    (cursor) => revenueApi.transactions({
      cursor,
      limit: TRANSACTIONS_PAGE_SIZE,
      sort: 'createdAt:desc',
    }),
    {
      key: 'revenue:transactions',
      getNextCursor: (page) => page?.nextCursor || page?.meta?.nextCursor,
      cacheTTL: 30000,
    }
  );

  // Infinite scroll observer
  const { ref: loadMoreRef } = useIntersectionObserver({
    onIntersect: () => {
      if (hasMore && !isFetchingMore) {
        fetchMore();
      }
    },
    enabled: hasMore && !isFetchingMore,
  });

  // Compute stats from dashboard data
  const stats = useMemo(() => {
    const data = dashboardData || {};
    return [
      {
        label: 'MRR',
        value: formatCurrency(data.mrr || data.monthlyRecurringRevenue || 0),
        change: formatPercent(data.mrrGrowth),
        changeType: getChangeType(data.mrrGrowth),
        icon: 'dollar-sign',
      },
      {
        label: 'ARR',
        value: formatCurrency(data.arr || data.annualRecurringRevenue || 0),
        change: formatPercent(data.arrGrowth),
        changeType: getChangeType(data.arrGrowth),
        icon: 'trending-up',
      },
      {
        label: 'Avg Revenue/Tenant',
        value: formatCurrency(data.arpt || data.avgRevenuePerTenant || 0),
        change: formatPercent(data.arptGrowth),
        changeType: getChangeType(data.arptGrowth),
        icon: 'user',
      },
      {
        label: 'Churn Rate',
        value: `${data.churnRate || 0}%`,
        change: data.churnChange ? `${data.churnChange > 0 ? '+' : ''}${data.churnChange}%` : undefined,
        changeType: data.churnChange > 0 ? 'negative' : 'positive',
        icon: 'user-minus',
      },
    ];
  }, [dashboardData]);

  // Plan breakdown
  const planBreakdown = useMemo(() => {
    const data = planBreakdownData || {};
    const plans = data.plans || data.breakdown || data.data || [];

    // Calculate total for percentages
    const total = plans.reduce((sum, p) => sum + (p.revenue || 0), 0);

    return plans.map(plan => ({
      ...plan,
      percentage: total > 0 ? Math.round((plan.revenue / total) * 100) : 0,
    }));
  }, [planBreakdownData]);

  // Trend data
  const trendPoints = useMemo(() => {
    const data = trendData || {};
    return data.points || data.trend || data.data || [];
  }, [trendData]);

  // Extract transactions
  const transactionsList = useMemo(() => {
    return transactions || [];
  }, [transactions]);

  // Handlers
  const handleRefresh = useCallback(() => {
    refetchDashboard();
    refetchTransactions();
    showToast('Revenue data refreshed', 'success');
  }, [refetchDashboard, refetchTransactions, showToast]);

  const handleExport = useCallback(async () => {
    try {
      showToast('Preparing export...', 'info');
      const data = await revenueApi.export({ format: 'csv' });

      // Create download
      const blob = new Blob([data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `revenue-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showToast('Export downloaded', 'success');
    } catch (error) {
      showToast(error.message || 'Export failed', 'error');
    }
  }, [showToast]);

  const handleTrendPeriodChange = useCallback((period) => {
    setTrendPeriod(period);
  }, []);

  // Helpers
  function formatCurrency(amount) {
    if (!amount) return '$0';
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(amount >= 10000 ? 0 : 1)}K`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  function formatPercent(value) {
    if (value === null || value === undefined) return undefined;
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
  }

  function getChangeType(value) {
    if (value === null || value === undefined) return 'neutral';
    return value >= 0 ? 'positive' : 'negative';
  }

  function formatDate(timestamp) {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  const isLoading = loadingDashboard && !dashboardData;

  return (
    <div className="revenue-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Revenue Analytics</h1>
          <p className="page-subtitle">Track revenue, subscriptions, and billing</p>
        </div>
        <div className="page-actions">
          <Button
            variant="secondary"
            icon="refresh"
            onClick={handleRefresh}
            disabled={loadingDashboard}
          >
            {loadingDashboard ? 'Syncing...' : 'Refresh'}
          </Button>
          <Button
            variant="secondary"
            icon="download"
            onClick={handleExport}
          >
            Export
          </Button>
          <Button variant="primary" icon="dollar-sign">
            Billing Portal
          </Button>
        </div>
      </div>

      {/* Error Banner */}
      {dashboardError && (
        <div className="alert alert-warning">
          <Icon name="alert-circle" size="sm" />
          <span>Failed to load some data. Showing cached information.</span>
          <Button variant="ghost" size="sm" onClick={handleRefresh}>Retry</Button>
        </div>
      )}

      {/* Stats Row */}
      <div className="stats-row">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} loading={isLoading} />
        ))}
      </div>

      <div className="revenue-grid">
        {/* Revenue by Plan */}
        <section className="section">
          <div className="section-header">
            <h3 className="section-title">Revenue by Plan</h3>
          </div>

          {loadingPlanBreakdown && planBreakdown.length === 0 ? (
            <div className="plan-breakdown-loading">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="plan-row skeleton">
                  <div className="skeleton-line" style={{ width: '100px' }}></div>
                  <div className="skeleton-bar"></div>
                  <div className="skeleton-line" style={{ width: '80px' }}></div>
                </div>
              ))}
            </div>
          ) : planBreakdown.length > 0 ? (
            <div className="plan-breakdown">
              {planBreakdown.map((item, index) => (
                <div key={index} className="plan-row">
                  <div className="plan-info">
                    <div className="plan-name">{item.plan || item.name}</div>
                    <div className="plan-count">{item.count || item.tenantCount || 0} tenants</div>
                  </div>
                  <div className="plan-bar-container">
                    <div
                      className="plan-bar"
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                  <div className="plan-revenue">
                    {formatCurrency(item.revenue)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="plan-breakdown-empty">
              <p>No revenue data available</p>
            </div>
          )}
        </section>

        {/* Revenue Trend */}
        <section className="section">
          <div className="section-header">
            <h3 className="section-title">Revenue Trend</h3>
            <div className="chart-controls">
              <button
                className={`chart-btn ${trendPeriod === 'monthly' ? 'active' : ''}`}
                onClick={() => handleTrendPeriodChange('monthly')}
              >
                Monthly
              </button>
              <button
                className={`chart-btn ${trendPeriod === 'quarterly' ? 'active' : ''}`}
                onClick={() => handleTrendPeriodChange('quarterly')}
              >
                Quarterly
              </button>
              <button
                className={`chart-btn ${trendPeriod === 'yearly' ? 'active' : ''}`}
                onClick={() => handleTrendPeriodChange('yearly')}
              >
                Yearly
              </button>
            </div>
          </div>

          {loadingTrend ? (
            <div className="chart-loading">
              <div className="loading-spinner">Loading chart...</div>
            </div>
          ) : trendPoints.length > 0 ? (
            <div className="chart-container">
              <div className="simple-chart">
                {trendPoints.slice(-12).map((point, index) => (
                  <div key={index} className="chart-bar-wrapper">
                    <div
                      className="chart-bar"
                      style={{
                        height: `${Math.max(10, (point.revenue / Math.max(...trendPoints.map(p => p.revenue))) * 100)}%`
                      }}
                      title={`${point.label || point.period}: ${formatCurrency(point.revenue)}`}
                    />
                    <span className="chart-label">{point.label || point.period}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="chart-placeholder">
              <Icon name="trending-up" size="xl" />
              <p>No trend data available</p>
            </div>
          )}
        </section>
      </div>

      {/* Recent Transactions */}
      <section className="section">
        <div className="section-header">
          <h3 className="section-title">Recent Transactions</h3>
          <Button variant="ghost" size="sm" onClick={refetchTransactions}>
            Refresh
          </Button>
        </div>

        <div className="transactions-table-container">
          {loadingTransactions && transactionsList.length === 0 ? (
            <div className="transactions-loading">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="transaction-row skeleton">
                  <div className="skeleton-line" style={{ width: '150px' }}></div>
                  <div className="skeleton-line" style={{ width: '100px' }}></div>
                  <div className="skeleton-line" style={{ width: '80px' }}></div>
                  <div className="skeleton-line" style={{ width: '80px' }}></div>
                  <div className="skeleton-line" style={{ width: '60px' }}></div>
                </div>
              ))}
            </div>
          ) : transactionsList.length > 0 ? (
            <>
              <table className="transactions-table">
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Type</th>
                    <th>Plan</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactionsList.map((tx, index) => (
                    <tr key={tx.id || index}>
                      <td className="tenant-cell">{tx.tenant || tx.company || tx.tenantName || '-'}</td>
                      <td>{tx.type || tx.transactionType || 'Subscription'}</td>
                      <td>{tx.plan || tx.planName || '-'}</td>
                      <td className="amount-cell">
                        {formatCurrency(tx.amount)}
                      </td>
                      <td>
                        <span className={`status-badge status-${tx.status || 'paid'}`}>
                          {tx.status || 'paid'}
                        </span>
                      </td>
                      <td className="date-cell">{formatDate(tx.createdAt || tx.date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Infinite scroll trigger */}
              {hasMore && (
                <div ref={loadMoreRef} className="transactions-load-more">
                  {isFetchingMore ? (
                    <div className="loading-spinner">Loading more...</div>
                  ) : (
                    <span>Scroll for more</span>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="transactions-empty">
              <Icon name="credit-card" size="xl" />
              <p>No transactions yet</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Revenue;
